import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../css/search.css";
import useBreakpoint from "../../utils/useBreakPoint";
import { searchService } from "../../services";
import { SkeletonText, SkeletonRows } from "../../component/Loading";
import { formatCurrencyOrUnit, formatPerUnit } from "../../utils/formatUnit";
import { translateSectorLabel } from "../../utils/sectorLabel";

const SearchResults = () => {
  const { t, i18n } = useTranslation();
  const PAGE_SIZE = 15;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const searchQuery = searchParams.get("q") || "";
  const searchType = searchParams.get("type") || "text"; // 'text' or 'ai'

  // View mode: 'list' or 'grid'
  const [viewMode, setViewMode] = useState("list");

  // Selected category for filtering
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [matchingLists, setMatchingLists] = useState([]);

  // AI summary expand/collapse state
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(false);

  // API state
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const loadMoreRef = useRef(null);
  const showResults = !loading && !error;

  // Fetch search results from API
  useEffect(() => {
    let cancelled = false;

    const fetchSearchResults = async () => {
      if (!searchQuery || !searchQuery.trim()) {
        setLoading(false);
        setLoadingMore(false);
        setError(null);
        setSearchResults([]);
        setAiSummary("");
        setMatchingLists([]);
        setHasMore(false);
        setNextOffset(0);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await searchService.search({
          query: searchQuery,
          type: searchType,
          limit: PAGE_SIZE,
          offset: 0,
        });

        const results = Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
            ? response.results
            : [];
        if (cancelled) return;

        setSearchResults(results);
        setAiSummary(response?.ai_summary || (searchType === "ai" ? "" : ""));
        const sectors = Array.isArray(response?.sectors) ? response.sectors : [];
        setMatchingLists(
          sectors.map((s) => ({
            id: s.id,
            name: s.name,
            count: s.material_count || s.count || results.length,
          }))
        );
        const responsePagination = response?.pagination || {};
        const nextOffsetValue =
          typeof responsePagination.next_offset === "number"
            ? responsePagination.next_offset
            : results.length;
        setHasMore(Boolean(responsePagination.has_more));
        setNextOffset(nextOffsetValue);
      } catch (err) {
        if (cancelled) return;
        console.error("Search error:", err);
        setError(err.message || t("search.error.fetch"));
        setSearchResults([]);
        setAiSummary("");
        setMatchingLists([]);
        setHasMore(false);
        setNextOffset(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSearchResults();
    return () => {
      cancelled = true;
    };
  }, [PAGE_SIZE, searchQuery, searchType, t]);

  const loadMoreResults = useCallback(async () => {
    if (loading || loadingMore || !hasMore || !searchQuery || !searchQuery.trim()) return;

    setLoadingMore(true);
    try {
      const response = await searchService.search({
        query: searchQuery,
        type: searchType,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });

      const newResults = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
          ? response.results
          : [];

      setSearchResults((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        const deduped = newResults.filter((item) => !seen.has(item.id));
        return [...prev, ...deduped];
      });

      const responsePagination = response?.pagination || {};
      const nextOffsetValue =
        typeof responsePagination.next_offset === "number"
          ? responsePagination.next_offset
          : nextOffset + newResults.length;
      setHasMore(Boolean(responsePagination.has_more));
      setNextOffset(nextOffsetValue);
    } catch (err) {
      console.error("Search pagination error:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [PAGE_SIZE, hasMore, loading, loadingMore, nextOffset, searchQuery, searchType]);

  useEffect(() => {
    if (!loadMoreRef.current || loading || !showResults || !hasMore) return;
    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreResults();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMoreResults, loading, showResults]);

  // Transform API results to match UI structure
  const allMatchingMaterials = useMemo(() => {
    return searchResults.map((result) => {
      // Extract last 8-10 datapoints for chart (or use all if less)
      const chartData = (result.datapoints || [])
        .slice(-10)
        .map(dp => dp.value);

      // Format date
      const lastDate = result.last_date
        ? new Date(result.last_date).toLocaleDateString("de-DE")
        : t("common.na");

      return {
        id: result.id,
        code: result.code,
        name: result.name,
        description: result.description,
        date: lastDate,
        value: result.last_value || 0,
        // Ohne Waehrung nur die Einheit (kein " / GWh")
        currencyUnit: formatPerUnit(result.currency, result.value_unit),
        // Kurzform fuer die Karte: Waehrung, ohne Waehrung die Einheit
        priceSuffix: formatCurrencyOrUnit(result.currency, result.value_unit),
        chartData: chartData,
      };
    });
  }, [searchResults, t]);

  // Filter matching materials by selected category
  // TODO: Implement proper filtering when backend provides category data
  const filteredMaterials = useMemo(() => {
    // For now, just return all materials (filtering disabled with dummy categories)
    return allMatchingMaterials;
  }, [allMatchingMaterials]);

  // Handle category click -> navigate to sector page
  const handleCategoryClick = (list) => {
    if (!list) return;
    setSelectedCategory(list.name);
    if (list.id) {
      navigate(`/charts/sector/${list.id}`, { state: { sectorName: list.name } });
    }
  };

  // Handle material item click
  const handleMaterialClick = (material) => {
    // Navigate to detail page using material code
    navigate(`/charts/material/${material.code}`);
  };

  // Simple inline chart using SVG - full width with gradient
  const renderMiniChart = (data, uniqueId) => {
    if (!data || data.length === 0) return null;

    const height = 120;
    const paddingTop = 10;
    const paddingBottom = 10;
    const paddingLeft = 0;
    const paddingRight = 0;

    const chartWidth = 100 - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    // Generate line points
    const linePoints = data
      .map((value, index) => {
        const x = paddingLeft + (index / (data.length - 1)) * chartWidth;
        const y = paddingTop + (chartHeight - ((value - min) / range) * chartHeight);
        return `${x},${y}`;
      })
      .join(" ");

    // Generate filled area points (includes bottom corners)
    const areaPoints = `${paddingLeft},${height - paddingBottom} ${linePoints} ${chartWidth},${height - paddingBottom}`;

    return (
      <svg
        width="100%"
        height={height}
        className="material-chart"
        preserveAspectRatio="none"
        viewBox={`0 0 100 ${height}`}
      >
        <defs>
          <linearGradient id={`gradient-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: "#8B5CF6", stopOpacity: 0.5 }} />
            <stop offset="100%" style={{ stopColor: "#8B5CF6", stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {/* Gradient fill area */}
        <polygon
          fill={`url(#gradient-${uniqueId})`}
          points={areaPoints}
        />
        {/* Line on top */}
        <polyline
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="0.5"
          points={linePoints}
        />
      </svg>
    );
  };

  return (
    <div className="search-results-container">
      <div className="search-content-wrapper">
        {/* Main Content Area */}
        <div className="search-main-content">
          {/* Header with title and view toggle */}
          <div className="search-header">
            <h2 className="search-title">{t("search.title")}</h2>
            {showResults && (
              <div className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label={t("search.view.grid")}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                  <rect x="12" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                  <rect x="2" y="12" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                  <rect x="12" y="12" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label={t("search.view.list")}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="3" width="16" height="2" rx="1" fill="currentColor" />
                  <rect x="2" y="9" width="16" height="2" rx="1" fill="currentColor" />
                  <rect x="2" y="15" width="16" height="2" rx="1" fill="currentColor" />
                </svg>
              </button>
            </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="search-loading">
              <div className="skeleton-card mb-3">
                <SkeletonText lines={2} lineHeight={16} gap={8} widths={["50%", "35%"]} />
              </div>
              <div className="skeleton-card">
                <SkeletonRows rows={4} columns={4} />
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="search-error">
              <p className="error-message">{t("search.error.prefix")} {error}</p>
              <button
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                {t("search.retry")}
              </button>
            </div>
          )}

          {/* Search query display */}
          {showResults && searchQuery && (
            <div className="search-query-display">
              {t("search.showingFor")} <span className="query-text">"{searchQuery}"</span>
            </div>
          )}

          {/* AI Summary Section - Only for AI search type */}
          {showResults && searchType === "ai" && aiSummary && (
            <div className="ai-summary-section">
              <div className="ai-summary-header">
                <h3 className="ai-summary-title">{searchQuery}</h3>
              </div>
              <div className={`ai-summary-content ${aiSummaryExpanded ? "expanded" : ""}`}>
                <p className="ai-summary-text">{aiSummary}</p>
              </div>
              {aiSummary.length > 300 && (
                <button
                  className="ai-summary-toggle"
                  onClick={() => setAiSummaryExpanded(!aiSummaryExpanded)}
                >
                  {aiSummaryExpanded ? t("search.ai.showLess") : t("search.ai.showMore")}
                </button>
              )}
            </div>
          )}

          {/* Category filter indicator - Disabled until backend provides category data */}
          {/* {!loading && !error && selectedCategory && (
            <div className="filter-indicator">
              Filtered by: <span className="filter-text">{selectedCategory}</span>
              <button className="clear-filter" onClick={() => setSelectedCategory(null)}>
                ✕
              </button>
            </div>
          )} */}

          {/* List View */}
          {showResults && viewMode === "list" && (
            <div className="search-list-view">
              <table className="search-table">
                <thead>
                  <tr>
                    <th>{t("search.table.name")}</th>
                    <th>{t("search.table.date")}</th>
                    <th>{t("search.table.value")}</th>
                    <th>{t("search.table.currencyUnit")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaterials.map((material) => (
                    <tr
                      key={material.id}
                      className="search-table-row"
                      onClick={() => handleMaterialClick(material)}
                    >
                      <td className="name-cell">{material.name}</td>
                      <td>{material.date}</td>
                      <td>{material.value.toFixed(2)}</td>
                      <td>{material.currencyUnit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Grid/Card View */}
          {showResults && viewMode === "grid" && (
            <div className="search-materials-grid">
              {filteredMaterials.map((material) => (
                <div
                  key={material.id}
                  className="material-card"
                  onClick={() => handleMaterialClick(material)}
                >
                  {/* Value and Date - Top Right */}
                  <div className="material-header">
                    <div className="material-price">
                      {material.value.toFixed(2)} {material.priceSuffix}
                    </div>
                    <div className="material-date">{material.date}</div>
                  </div>

                  {/* Chart - Center, Full Width */}
                  <div className="material-chart-wrapper">
                    {renderMiniChart(material.chartData, material.id)}
                  </div>

                  {/* Material Name - Bottom Left */}
                  <div className="material-footer">
                    <div className="material-name">{material.name.split(" (inkl")[0]}</div>
                    <div className="material-subtitle">{t("search.materialSubtitle")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No results message */}
          {showResults && filteredMaterials.length === 0 && searchQuery && (
            <div className="no-results">
              <p>{t("search.noResults", { query: searchQuery })}</p>
            </div>
          )}

          {showResults && hasMore && (
            <div ref={loadMoreRef} style={{ minHeight: 24, padding: "8px 0", textAlign: "center", color: "#666" }}>
              {loadingMore ? t("common.loading") : ""}
            </div>
          )}
        </div>

        {/* Right Sidebar - Matching Lists */}
        {loading && (
        <div className="search-sidebar">
          <div className="skeleton-card mb-3">
            <SkeletonText lines={1} lineHeight={16} widths={["50%"]} />
          </div>
          {Array.from({ length: 4 }).map((_, idx) => (
            <div key={idx} className="skeleton-card mb-3">
              <SkeletonText lines={2} lineHeight={14} gap={8} widths={["70%", "40%"]} />
            </div>
          ))}
        </div>
        )}

        {showResults && (
        <div className="search-sidebar">
          <h3 className="sidebar-title">{t("search.matchingLists")}</h3>
          <div className="matching-lists">
            {matchingLists.map((list) => (
              <div
                key={list.id}
                className={`category-card ${selectedCategory === list.name ? "selected" : ""}`}
                onClick={() => handleCategoryClick(list)}
              >
                <div className="category-name">{translateSectorLabel(list.name, i18n.resolvedLanguage || i18n.language)}</div>
                <div className="category-count">{t("search.materialCount", { count: list.count })}</div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
